/**
 * The bridge between the passphrase adapter and the surface that can ask.
 *
 * `DeviceVerificationPort.verify()` returns a promise and cannot render a text
 * field. The field belongs on the concealment curtain — the one surface already
 * covering the content, and the only one guaranteed to be in front of the user
 * when a passphrase is wanted. So the adapter is constructed with `prompt`, the
 * curtain subscribes to `current()`, and the promise resolves when the user
 * submits or declines.
 *
 * There is **no queue**. A second challenge raised while one is pending
 * resolves `null` rather than stacking: two passphrase fields cannot both be in
 * front of the user, and a queued one would appear after the moment that asked
 * for it had passed — a prompt with no visible cause is a prompt people answer
 * without knowing what they are authorizing.
 */

import type { Cancel, JournalAuthReason } from "@ff/domain/ports";
import type { PassphrasePrompt } from "../platform/auth";

export interface PassphraseChallenge {
  readonly reason: JournalAuthReason;
  /** Resolves the waiting `verify()` with what the user typed. */
  submit(passphrase: string): void;
  /** "Not now". Resolves the waiting `verify()` as a refusal, not an error. */
  cancel(): void;
}

interface PendingChallenge {
  readonly challenge: PassphraseChallenge;
  readonly settle: (passphrase: string | null) => void;
}

export class PassphraseChallengeBroker {
  #pending: PendingChallenge | null = null;
  #listeners = new Set<() => void>();

  /** Passed to the passphrase adapter as its `PassphrasePrompt`. */
  readonly prompt: PassphrasePrompt = (reason) =>
    new Promise<string | null>((resolve) => {
      if (this.#pending !== null) {
        resolve(null);
        return;
      }
      const settle = (passphrase: string | null): void => {
        // Cleared before resolving, so the continuation of `verify()` observes
        // a broker with nothing outstanding and can raise the next challenge.
        if (this.#pending?.settle !== settle) return;
        this.#pending = null;
        this.#notify();
        resolve(passphrase);
      };
      const challenge: PassphraseChallenge = {
        reason,
        submit: (passphrase) => {
          settle(passphrase);
        },
        cancel: () => {
          settle(null);
        },
      };
      this.#pending = { challenge, settle };
      this.#notify();
    });

  current(): PassphraseChallenge | null {
    return this.#pending?.challenge ?? null;
  }

  subscribe(listener: () => void): Cancel {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /**
   * Abandons anything outstanding. Called when the page is concealed: a
   * passphrase field left open across a foreground loss would be answered into
   * a session that has already revoked its lease.
   */
  abandon(): void {
    this.#pending?.settle(null);
  }

  #notify(): void {
    for (const listener of [...this.#listeners]) listener();
  }
}
